import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from 'react-router-dom';

const Services = () => {
  const navigate = useNavigate();
  const services = [
    {
      icon: "🏢",
      num: "01",
      title: "Establishment Compliance",
      desc: "Complete registration and licensing for commercial establishments under the Shops & Establishments Act across all states.",
      points: [
        "New establishment registrations across all states",
        "Annual licence renewals and amendments",
        "Employee register maintenance (state-specific)",
        "Audit-ready digital documentation",
      ],
      tags: ["Registration", "Licensing", "Multi-state"],
    },
    {
      icon: "🏭",
      num: "02",
      title: "Factory Compliance",
      desc: "Industrial regulations and safety standards management under the Factories Act, 1948.",
      points: [
        "Factory registration and plan approval",
        "Annual licence renewals",
        "HSE compliance",
        "Mock inspection support",
      ],
      tags: ["Factories Act", "HSE", "Inspections"],
    },
    {
      icon: "👥",
      num: "03",
      title: "Contract Labour Compliance",
      desc: "Complete CLRA compliance for employers and contractors.",
      points: [
        "CLRA registration",
        "Contractor verification",
        "Statutory registers",
        "Wage & attendance records",
      ],
      tags: ["CLRA", "Central & State"],
    },
    {
      icon: "💰",
      num: "04",
      title: "Payroll Services",
      desc: "End-to-end managed payroll with high accuracy.",
      points: [
        "Monthly payroll processing",
        "Bonus & incentives",
        "Full & Final settlement",
        "Form 16 generation",
      ],
      tags: ["Payroll", "Salary"],
    },
    {
      icon: "📊",
      num: "05",
      title: "Payroll Compliance",
      desc: "PF, ESI, PT, TDS management with zero penalties.",
      points: [
        "PF filings",
        "ESI contributions",
        "PT deductions",
        "TDS returns",
      ],
      tags: ["PF", "ESI", "TDS"],
    },
    {
      icon: "🪪",
      num: "06",
      title: "Industrial Licensing",
      desc: "Complete licensing support including pollution and fire NOCs.",
      points: [
        "Factory & trade licence",
        "Pollution control approvals",
        "Fire NOC",
        "Boiler licences",
      ],
      tags: ["Licensing", "Renewals"],
    },
    {
      icon: "🔍",
      num: "07",
      title: "Consulting & Audit",
      desc: "Proactive audits to prevent compliance failures.",
      points: [
        "Multi-law audits",
        "Risk analysis",
        "Corrective planning",
        "Compliance reporting",
      ],
      tags: ["Audit", "Risk"],
    },
    {
      icon: "🔎",
      num: "08",
      title: "Inspection Handling",
      desc: "Full support during inspections.",
      points: [
        "Documentation review",
        "On-site support",
        "Notice handling",
        "Inspector liaison",
      ],
      tags: ["Inspection", "Support"],
    },
    {
      icon: "⚖️",
      num: "09",
      title: "Compliance Legal Services",
      desc: "Legal support for compliance-related issues.",
      points: [
        "Notice drafting",
        "Summons handling",
        "Penalty mitigation",
        "Regularisation",
      ],
      tags: ["Legal", "Litigation"],
    },
  ];

  const handleFreeConsultation = () => navigate('/contact');
  const handleSeeProducts = () => navigate('/products');

  const industries = [
    { icon: "🏗️", name: "Manufacturing" },
    { icon: "💻", name: "IT / ITES" },
    { icon: "🛒", name: "Retail" },
    { icon: "🚚", name: "Logistics" },
    { icon: "🏦", name: "BFSI" },
    { icon: "🏥", name: "Healthcare" },
    { icon: "🍽️", name: "Hospitality" },
    { icon: "📦", name: "E-Commerce" },
  ];

  return (
    <div className="bg-white text-black">

      {/* SERVICES */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">

          {/* HEADER */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div>
              <p className="text-[#d97925] uppercase text-sm font-semibold">
                What We Do
              </p>
              <h2 className="text-4xl md:text-5xl font-bold mt-2">
                9 Core Compliance <br /> Service Domains
              </h2>
            </div>

            <div className="text-gray-600 text-lg">
              From establishment to payroll, factory to litigation — we handle
              every aspect of statutory compliance across 18+ Indian states.
            </div>
          </div>

          {/* GRID */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((svc, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -8, scale: 1.02 }}
                className="border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-lg transition"
              >
                {/* TOP */}
                <div className="flex justify-between items-center mb-4">
                  <div className="text-2xl">{svc.icon}</div>
                  <div className="text-gray-400 font-semibold">{svc.num}</div>
                </div>

                {/* TITLE */}
                <h3 className="text-xl font-bold mb-2">{svc.title}</h3>

                {/* DESC */}
                <p className="text-gray-600 mb-4">{svc.desc}</p>

                {/* POINTS */}
                <div className="space-y-2 mb-4">
                  {svc.points.map((point, idx) => (
                    <div key={idx} className="flex gap-2 text-sm text-gray-700">
                      <span className="text-[#d97925]">→</span>
                      <span>{point}</span>
                    </div>
                  ))}
                </div>

                {/* TAGS */}
                <div className="flex flex-wrap gap-2">
                  {svc.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="text-xs bg-gray-100 px-2 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">

          <p className="text-[#d97925] uppercase text-sm font-semibold mb-2">
            Industries Served
          </p>
          <h3 className="text-3xl font-bold mb-10">
            Compliance solutions for every sector
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((ind, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05 }}
                className="border border-gray-200 rounded-xl p-6 text-center shadow-sm hover:shadow-md"
              >
                <div className="text-3xl mb-2">{ind.icon}</div>
                <div className="font-semibold">{ind.name}</div>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* FINAL CTA - Updated */}
            <section className="py-28 bg-gradient-to-br from-[#111111] to-[#111111] text-white">
              <div className="max-w-5xl mx-auto px-6 text-center">
                <h2 className="text-5xl font-bold tracking-tight mb-6">Simplify your Compliance</h2>
                <p className="text-xl text-emerald-100 mb-12 max-w-xl mx-auto">
                  We help you navigate compliance with clarity, care and confidence.
                </p>
      
                <div className="flex flex-col sm:flex-row gap-5 justify-center">
                  <motion.button
                    whileHover={{ scale: 1.08 }}
                    onClick={handleFreeConsultation}   // ← Goes to Contact Us
                    className="px-14 py-7 bg-white text-[#d97925] font-semibold text-xl rounded-3xl shadow-xl"
                  >
                    Request a Free Consultation
                  </motion.button>
                </div>
              </div>
            </section>

    </div>
  );
};

export default Services;